import { useState, useEffect, useRef, useCallback } from "react";
import type { SseClient } from "../../api/SseClient";
import { createSseClient } from "../../api/factory/CreateSseClient";

export interface QueueState {
  queueStatus: "active" | "paused";
  articleQueuedCount: number;
  homepageQueuedCount: number;
}

export interface PublicationQueueState extends QueueState {
  sitewideQueuedCount: number;
}

export interface CounterState {
  queuedCount: number;
}

export interface DashboardData {
  generation: QueueState;
  publication: PublicationQueueState;
  publicationCheck: CounterState;
  siteVerification: CounterState;
  siteVerificationFanout: CounterState;
  proxyVerification: CounterState;
  siteParamsCollection: CounterState;
  indexing: CounterState;
  competitorLinkScan: CounterState;
  competitorLinkRemoval: CounterState;
}

export type ScreenState =
  | { kind: "loading" }
  | { kind: "data"; data: DashboardData }
  | { kind: "error" };

export interface UseDashboardSubscriptionResult {
  state: ScreenState;
}

export function useDashboardSubscription(): UseDashboardSubscriptionResult {
  const [state, setState] = useState<ScreenState>({ kind: "loading" });
  const clientRef = useRef<SseClient | null>(null);

  const applyPayload = useCallback((raw: unknown) => {
    const data = raw as DashboardData;
    setState({ kind: "data", data });
  }, []);

  useEffect(() => {
    const client = createSseClient({
      url: "/api/dashboard/subscribe",
      events: {
        initial: applyPayload,
        update: applyPayload,
      },
      onError() {
        setState({ kind: "error" });
      },
    });

    clientRef.current = client;

    return () => {
      client.close();
      clientRef.current = null;
    };
  }, [applyPayload]);

  return { state };
}
